/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IImageSettings, Image } from '@msdyn365-commerce/core';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';

import { IDlvryBannerBlockData } from './dlvry-banner-block.data';
import { IDlvryBannerBlockProps } from './dlvry-banner-block.props.autogenerated';

export interface IDlvryBannerBlockViewProps extends IDlvryBannerBlockProps<IDlvryBannerBlockData> {
    BannerBlock: IModuleProps & React.HTMLAttributes<HTMLDivElement>;
    image: JSX.Element | null;
    imageLink: React.AnchorHTMLAttributes<HTMLAnchorElement> & {
        tag: keyof JSX.IntrinsicElements;
        className: string;
    } | null;
    content: JSX.Element | null;
}

/**
 *
 * DlvryBannerBlock component
 * @extends {React.PureComponent<IDlvryBannerBlockProps<IDlvryBannerBlockData>>}
 */
class DlvryBannerBlock extends React.PureComponent<IDlvryBannerBlockProps<IDlvryBannerBlockData>> {

    private baseClassName = 'dlvry-banner-block';

    public render(): JSX.Element | null {
        const viewProps: IDlvryBannerBlockViewProps = {
            ...this.props,
            BannerBlock: {
                className: this.baseClassName,
                moduleProps: this.props,
                style: {
                    ['--overlay-color' as any]: this.props.config.overlayColor ? this.props.config.overlayColor : undefined,
                    ['--overlay-opacity' as any]: this.props.config.overlayOpacity ? `${this.props.config.overlayOpacity / 100}` : undefined
                }
            },
            image: this.renderImage(),
            imageLink: this.renderImageLink(),
            content: this.renderContent()
        };
        return this.props.renderView(viewProps);
    }

    private renderImage(): JSX.Element | null {
        const { config, context } = this.props;

        const height = 350;
        const width = 1410;
        const imageSettings: IImageSettings = {
            viewports: {
                xs: { h: height, w: width, q: `h=${height}&w=${width}` },
                sm: { h: height, w: width, q: `h=${height}&w=${width}` },
                md: { h: height, w: width, q: `h=${height}&w=${width}` },
                lg: { h: height, w: width, q: `h=${height}&w=${width}` },
                xl: { h: height, w: width, q: `h=${height}&w=${width}` }
            },
            disableLazyLoad: config.image?.imageSettings?.disableLazyLoad ?? false,
            lazyload: config.image?.imageSettings?.lazyload ?? true
        };

        return (
            <Image
                className={`${this.baseClassName}__image`}
                src={config.image?.src}
                alt={config.image?.altText}
                requestContext={context.request}
                gridSettings={context.request.gridSettings!}
                imageSettings={imageSettings}
            />
        );
    }

    private renderImageLink(): IDlvryBannerBlockViewProps['imageLink'] {
        const { config } = this.props;

        if (!config.imageLink) {
            return null;
        }

        const { linkText, linkUrl, ariaLabel, openInNewTab } = config.imageLink;

        return {
            tag: 'a',
            href: linkUrl?.destinationUrl,
            target: openInNewTab ? '_blank' : '_self',
            'aria-label': ariaLabel || linkText,
            className: `${this.baseClassName}__image-link`,
            rel: openInNewTab ? 'noopener noreferrer' : undefined
        };
    }

    private renderContent(): JSX.Element | null {
        return null;
    }
}

export default DlvryBannerBlock;
